// src/components/assets/AssetsTable.tsx
import React from 'react';
import { Edit, Trash2, X } from 'lucide-react';
import { AssetData, ColumnKey } from '../../pages/Assets'; // Adjust path

interface AssetsTableProps {
    paginatedData: AssetData[];
    visibleColumns: ColumnKey[];
    getColumnDisplayName: (col: ColumnKey) => string;
    getSortIndicator: (key: ColumnKey) => string;
    handleSort: (key: ColumnKey) => void;
    columnFilters: Partial<Record<ColumnKey, string>>;
    setColumnFilters: React.Dispatch<React.SetStateAction<Partial<Record<ColumnKey, string>>>>;
    selectedRows: string[];
    handleSelectAll: () => void;
    handleRowSelect: (id: string) => void;
    handleEdit: (asset: AssetData) => void;
    handleDelete: (id: string) => void;
    formatCurrency: (value: string | number) => string;
    formatDate: (date: string) => string;
    getLabel: (group: string, value: string) => string;
    lookupGroupMap: Record<string, string>;
    // Props for inline editing (optional, can be expanded)
    inlineEditingId: string | null;
    setInlineEditingId: React.Dispatch<React.SetStateAction<string | null>>;
}

const AssetsTable: React.FC<AssetsTableProps> = (props) => {
    const {
        paginatedData, visibleColumns, getColumnDisplayName, getSortIndicator, handleSort,
        columnFilters, setColumnFilters, selectedRows, handleSelectAll, handleRowSelect,
        handleEdit, handleDelete, formatCurrency, formatDate, getLabel, lookupGroupMap,
        inlineEditingId, setInlineEditingId
    } = props;
    

    return (
        <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 min-w-max">
                <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                            <input type="checkbox" checked={selectedRows.length === paginatedData.length && paginatedData.length > 0} onChange={handleSelectAll} className="rounded text-green-600 focus:ring-green-600" />
                        </th>
                        {visibleColumns.map(col => (
                            <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 transition-colors">
                                <div className="flex items-center justify-between" onClick={() => handleSort(col)}>
                                    <span className="mr-2">{getColumnDisplayName(col)}</span>
                                    <span className="text-xs text-gray-400">{getSortIndicator(col)}</span>
                                </div>
                                <input
                                    type="text"
                                    placeholder={`Filter ${getColumnDisplayName(col)}`}
                                    value={columnFilters[col] || ''}
                                    onClick={(e) => e.stopPropagation()} // Prevent sort on filter click
                                    onChange={(e) => setColumnFilters(prev => ({ ...prev, [col]: e.target.value }))}
                                    className="mt-1 w-full p-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-green-600"
                                />
                            </th>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((asset: AssetData) => (
                        <tr key={asset.id} className={`hover:bg-gray-50 transition-colors ${selectedRows.includes(asset.id) ? 'bg-green-50' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <input type="checkbox" checked={selectedRows.includes(asset.id)} onChange={() => handleRowSelect(asset.id)} className="rounded text-green-600 focus:ring-green-600" />
                            </td>
                            {visibleColumns.map(col => (
                                <td key={col} className="px-6 py-4 whitespace-nowrap text-sm">
                                    {['assetOwnerType', 'assetType', 'assetSubType'].includes(col)
                                        ? getLabel(lookupGroupMap[col], asset[col as keyof AssetData] as string)
                                        : ['assetCurrentPrice', 'assetUnitPrice', 'assetspurchaseValue'].includes(col)
                                        ? <span className="text-green-600 font-medium">{formatCurrency(asset[col as keyof AssetData] as string)}</span>
                                        : col === 'assetspurchaseDate' ? formatDate(asset.assetspurchaseDate)
                                        : col === 'assetLoan' ? (asset.assetLoan ? 'Yes' : 'No')
                                        : String(asset[col as keyof AssetData]) || 'N/A'
                                    }
                                </td>
                            ))}
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                    <button onClick={() => handleEdit(asset)} className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(asset.id)} className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default AssetsTable;